/**
 * DashboardButton - Compact button for dashboard components
 *
 * A simple, consistent button used across dashboard sections:
 * - Approve all / Approve selected (primary variant)
 * - View full prep (secondary variant)
 */

import type { ButtonHTMLAttributes } from 'react';
import React from 'react';

import { cx } from '@/utils/cx';

type DashboardButtonVariant = 'primary' | 'secondary';

interface DashboardButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: DashboardButtonVariant;
  /** Button content */
  children: React.ReactNode;
}

const variantStyles: Record<DashboardButtonVariant, string> = {
  primary:
    'text-white bg-[var(--color-bg-brand-solid)] hover:bg-[var(--color-bg-brand-solid_hover)]',
  secondary: 'text-primary border border-secondary hover:bg-primary_hover',
};

/**
 * Compact button for the dashboard action row.
 */
export const DashboardButton = React.memo(function DashboardButton({
  variant = 'primary',
  children,
  className,
  ...props
}: DashboardButtonProps) {
  return (
    <button
      type='button'
      {...props}
      className={cx(
        'py-[3px] px-2.5 text-[13px] font-medium rounded-md transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-focus-ring',
        variantStyles[variant],
        className
      )}
    >
      {children}
    </button>
  );
});
